import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { Outcome } from '@sfpbxtestbot/contracts';

/** Plain-English wording for every outcome, used everywhere a badge appears. */
export const OUTCOME_LABEL: Record<Outcome, string> = {
  pending: 'Not started',
  running: 'In progress',
  passed: 'Passed',
  warning: 'Needs a look',
  failed: 'Failed',
  skipped: 'Skipped',
  blocked: 'Blocked',
};

export function StatusBadge({ outcome, label }: { outcome: Outcome; label?: string }): ReactNode {
  return (
    <span className={`badge badge--${outcome}`}>
      <span className="badge__dot" aria-hidden="true" />
      {label ?? OUTCOME_LABEL[outcome]}
    </span>
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'medium' | 'small';
  busy?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'medium',
  busy = false,
  children,
  disabled,
  ...rest
}: ButtonProps): ReactNode {
  return (
    <button
      type="button"
      className={`button button--${variant}${size === 'small' ? ' button--small' : ''}`}
      disabled={disabled || busy}
      {...rest}
    >
      {busy ? <span className="spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export function Card({
  title,
  subtitle,
  actions,
  flush = false,
  children,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  flush?: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <section className={`card${flush ? ' card--flush' : ''}`}>
      {title || actions ? (
        <header className="card__header" style={flush ? { padding: '20px 20px 0' } : undefined}>
          <div className="card__title">
            {typeof title === 'string' ? <h2>{title}</h2> : title}
            {subtitle ? <span className="card__subtitle">{subtitle}</span> : null}
          </div>
          {actions ? <div className="row">{actions}</div> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function PageHeader({
  title,
  lede,
  actions,
}: {
  title: string;
  lede?: ReactNode;
  actions?: ReactNode;
}): ReactNode {
  return (
    <header className="page-header">
      <div className="page-header__text">
        <h1>{title}</h1>
        {lede ? <p className="page-header__lede">{lede}</p> : null}
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </header>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: ReactNode;
  action?: ReactNode;
}): ReactNode {
  return (
    <div className="empty">
      <span className="empty__title">{title}</span>
      <p className="empty__body">{body}</p>
      {action}
    </div>
  );
}

export function Callout({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warning' | 'danger';
  children: ReactNode;
}): ReactNode {
  return <div className={`callout callout--${tone}`}>{children}</div>;
}

export function Stat({
  value,
  label,
  tone,
}: {
  value: ReactNode;
  label: string;
  tone?: 'passed' | 'failed' | 'warning';
}): ReactNode {
  return (
    <div className={`stat${tone ? ` stat--${tone}` : ''}`}>
      <span className="stat__value">{value}</span>
      <span className="stat__label">{label}</span>
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {error ? <span className="field__error">{error}</span> : null}
      {hint && !error ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

export function Checkbox({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}): ReactNode {
  return (
    <label className="checkbox">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className="checkbox__text">
        <span className="checkbox__label">{label}</span>
        {hint ? <span className="field__hint">{hint}</span> : null}
      </span>
    </label>
  );
}

/** Human-friendly duration: milliseconds are noise once you pass a minute. */
export function formatDuration(ms: number | undefined): string {
  if (ms == null) return '—';
  if (ms < 1_000) return `${ms} ms`;
  const seconds = ms / 1_000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  if (minutes < 60) return `${minutes}m ${remainder}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function formatTimestamp(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatRelative(value: string | undefined): string {
  if (!value) return '—';
  const elapsed = Date.now() - new Date(value).getTime();
  if (Number.isNaN(elapsed)) return '—';
  const minutes = Math.round(elapsed / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}
